import React, { createContext, useContext, useState, ReactNode, useEffect } from "react";
import { ClaimHelper, LocalStorageHelper } from "../assets/utils/helpers";

interface CommunityContextType {
    communityId: string | null;
    setCommunityId: (id: string | null) => void;
    clearCommunity: () => void;
}

const CommunityContext = createContext<CommunityContextType | undefined>(undefined);

const STORAGE_KEY = LocalStorageHelper.communityIdKey;

export const CommunityProvider = ({ children }: { children: ReactNode }) => {
    const [communityId, setCommunityIdState] = useState<string | null>(() => {
        if (typeof window === "undefined") return null;
        // Prefer claim if present
        const fromClaims = LocalStorageHelper.normalize(ClaimHelper.getCommunityId());
        if (fromClaims) {
            LocalStorageHelper.setCommunityId(fromClaims);
            return fromClaims;
        }

        // Fallback to stored value
        return LocalStorageHelper.getCommunityId();
    });

    const setCommunityId = (id: string | null) => {
        const normalizedId = LocalStorageHelper.normalize(id);
        setCommunityIdState(normalizedId);
        LocalStorageHelper.setCommunityId(normalizedId);
    };

    const clearCommunity = () => setCommunityId(null);

    useEffect(() => {
        const onStorage = (e: StorageEvent) => {
            if (e.key === STORAGE_KEY) setCommunityIdState(LocalStorageHelper.normalize(e.newValue));
        };
        window.addEventListener("storage", onStorage);
        return () => window.removeEventListener("storage", onStorage);
    }, []);

    return (
        <CommunityContext.Provider value={{ communityId, setCommunityId, clearCommunity }}>
            {children}
        </CommunityContext.Provider>
    );
};

export const useCommunity = () => {
    const ctx = useContext(CommunityContext);
    if (!ctx) throw new Error("useCommunity must be used within a CommunityProvider");
    return ctx;
};
